/**
 * /scout — Leaguemate Scouting UI (info-first facet drill-down, REVISED).
 *
 * Scan touches only the target league. Each member exposes lazy facets:
 *   [Other leagues] [Trades] [Waivers]  → RAW, un-interpreted data
 *   [Fingerprint]                        → the ONLY computed surface
 *
 * Thin shell over useScout + src/utils/scout/*. NO CHARTS. theme.js tokens,
 * sized for 75% browser zoom.
 */

import { useEffect, useState } from "react";
import { theme } from "../../data/theme.js";
import { useScout } from "../../hooks/useScout.js";
import { categorizeLeague, breakdownString } from "../../utils/scout/scoutCategorize.js";
import {
  REACH_STYLE, CONFIDENCE_STYLE, topLeverage, topPets, axisRows, bandColor,
  buildTradeLog, buildWaiverLog, fmtDate,
} from "./scoutPresenters.js";

const C = theme.colors;
const F = theme.fonts.almanac;
const SZ = { micro: 12, small: 13, body: 15, sub: 17, head: 22, hero: 30 };

const card = { background: C.bg.surface, border: `1px solid ${C.border.default}`, borderRadius: theme.radius.xl, padding: 16 };
const chip = (color) => ({ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 9px", borderRadius: 999, fontSize: SZ.micro, fontWeight: 700, color, background: `${color}1f`, border: `1px solid ${color}40`, whiteSpace: "nowrap" });
const posChip = (pos) => ({ ...chip(C.pos[pos] || C.text.muted), padding: "1px 6px", fontSize: 11 });

const FACETS = [
  { key: "otherLeagues", label: "Other leagues" },
  { key: "trades", label: "Trades" },
  { key: "waivers", label: "Waivers" },
  { key: "fingerprint", label: "Fingerprint" },
];

function Bar({ label, current, total }) {
  const pct = total ? Math.min(100, Math.round((current / total) * 100)) : null;
  return (
    <div style={{ width: "100%", maxWidth: 460, margin: "6px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: SZ.micro, color: C.text.secondary, marginBottom: 5 }}>
        <span>{label || "Loading…"}</span><span>{total ? `${current}/${total}` : ""}</span>
      </div>
      <div style={{ height: 6, borderRadius: 999, background: C.bg.raised, overflow: "hidden" }}>
        <div style={{ height: "100%", width: pct == null ? "40%" : `${pct}%`, background: C.brand.gradient, transition: "width .2s" }} />
      </div>
    </div>
  );
}

// ── Raw facet: Other leagues ───────────────────────────────────────────────
function OtherLeaguesPanel({ state }) {
  if (state?.loading) return <Bar label="Fetching their other leagues…" />;
  if (state?.error) return <div style={{ color: C.status.danger, fontSize: SZ.small }}>{state.error}</div>;
  const leagues = state?.leagues || [];
  if (!leagues.length) return <div style={{ fontSize: SZ.small, color: C.text.faint }}>No other NFL leagues found.</div>;
  const cats = leagues.map(categorizeLeague);
  return (
    <div>
      <div style={{ fontSize: SZ.small, color: C.text.secondary, marginBottom: 8 }}>{breakdownString(leagues)} <span style={{ color: C.text.faint }}>({leagues.length} total)</span></div>
      <div style={{ display: "grid", gap: 4 }}>
        {cats.map((c) => (
          <div key={c.league_id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: SZ.small, padding: "3px 0" }}>
            <span style={{ ...chip(C.text.muted), minWidth: 64, justifyContent: "center" }}>{c.type}</span>
            <span style={{ color: C.text.primary, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</span>
            <span style={{ color: C.text.faint }}>{c.format} · {c.teams}tm · {c.season} · {c.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Raw facet: Trade log ───────────────────────────────────────────────────
function side(items, picks, color) {
  return (
    <span>
      {items.map((p, i) => (
        <span key={p.player_id} style={{ color: C.text.primary }}>
          {i > 0 ? ", " : ""}<span style={{ color: C.pos[p.position] || C.text.primary }}>{p.name}</span>
          {p.value != null && <span style={{ color: C.extended.valueHighlight }}> ({p.value})</span>}
        </span>
      ))}
      {picks.map((pk, i) => <span key={`pk${i}`} style={{ color: C.text.muted }}>{(items.length || i) ? ", " : ""}{pk}</span>)}
      {!items.length && !picks.length && <span style={{ color: C.text.faint }}>—</span>}
    </span>
  );
}
function TradeLogPanel({ state, maps }) {
  if (state?.loading) return <Bar label="Pulling transactions…" />;
  if (state?.error) return <div style={{ color: C.status.danger, fontSize: SZ.small }}>{state.error}</div>;
  if (!state?.leagues) return null;
  const log = buildTradeLog(state.leagues, maps.playersMap || {}, maps.valueMap || new Map());
  if (!log.length) return <div style={{ fontSize: SZ.small, color: C.text.faint }}>No completed trades in the sampled dynasty/keeper leagues.</div>;
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div style={{ fontSize: SZ.micro, color: C.text.faint }}>{log.length} trades · raw chronological log (no verdict)</div>
      {log.map((t, i) => (
        <div key={i} style={{ borderLeft: `3px solid ${C.accents.green}`, paddingLeft: 10, fontSize: SZ.small }}>
          <div style={{ color: C.text.faint, fontSize: SZ.micro }}>{fmtDate(t.created)} · {t.league_name} ({t.season})</div>
          <div><span style={{ color: C.status.danger, fontWeight: 700 }}>gave</span> {side(t.gave, t.gavePicks)}</div>
          <div><span style={{ color: C.status.success, fontWeight: 700 }}>got</span>&nbsp; {side(t.got, t.gotPicks)}</div>
          {t.faab.map((f, j) => <div key={j} style={{ color: C.text.muted, fontSize: SZ.micro }}>FAAB {f.dir === "in" ? "received" : "sent"} ${f.amount}</div>)}
        </div>
      ))}
    </div>
  );
}

// ── Raw facet: Waiver log ──────────────────────────────────────────────────
function WaiverLogPanel({ state, maps }) {
  if (state?.loading) return <Bar label="Reading waivers…" />;
  if (state?.error) return <div style={{ color: C.status.danger, fontSize: SZ.small }}>{state.error}</div>;
  if (!state?.leagues) return null;
  const log = buildWaiverLog(state.leagues, maps.playersMap || {});
  if (!log.length) return <div style={{ fontSize: SZ.small, color: C.text.faint }}>No FAAB waiver claims in the sampled leagues.</div>;
  return (
    <div style={{ display: "grid", gap: 3 }}>
      <div style={{ fontSize: SZ.micro, color: C.text.faint }}>{log.length} winning waiver claims · raw log</div>
      {log.slice(0, 60).map((w, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: SZ.small, padding: "2px 0" }}>
          <span style={{ color: C.text.faint, fontSize: SZ.micro, width: 92 }}>{fmtDate(w.created)}</span>
          {w.position && <span style={posChip(w.position)}>{w.position}</span>}
          <span style={{ color: C.text.primary, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{w.player}</span>
          <span style={{ color: C.extended.valueHighlight, fontWeight: 700 }}>${w.bid}</span>
          <span style={{ color: C.text.faint, fontSize: SZ.micro }}>of ${w.budget} · {w.league_name}</span>
        </div>
      ))}
    </div>
  );
}

// ── Computed facet: Fingerprint (the only verdict surface) ─────────────────
function BelieverRow({ b, showAffinity }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0", fontSize: SZ.small }}>
      <span style={posChip(b.position)}>{b.position}</span>
      <span style={{ color: C.text.primary, fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {b.name}{b.acqBoost ? <span title="acquired via trade (capped boost)" style={{ color: C.status.success }}> ↑</span> : null}
      </span>
      <span style={{ color: C.extended.valueHighlight, fontVariantNumeric: "tabular-nums" }}>{b.value != null ? b.value : "—"}</span>
      <span style={{ color: C.text.faint, width: 78, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{showAffinity ? `aff ${b.adjustedAffinity ?? b.rate}` : `${Math.round((b.rate || 0) * 100)}% own`}</span>
    </div>
  );
}
function FingerprintPanel({ state, affinityMeta }) {
  const [tab, setTab] = useState("leverage");
  if (state?.loading) return <Bar label="Computing fingerprint (transactions + picks)…" />;
  if (state?.error) return <div style={{ color: C.status.danger, fontSize: SZ.small }}>{state.error}</div>;
  const dd = state?.dd;
  if (!dd) return null;
  const r = REACH_STYLE[dd.reachability?.band] || REACH_STYLE.dormant;
  const crown = dd.archetype?.archetype;
  const dormant = dd.reachability?.band === "dormant";
  const conf = CONFIDENCE_STYLE[affinityMeta?.confidence] || null;
  const believers = dd.netAffinity?.adjustedBelievers || [];
  const list = tab === "leverage" ? topLeverage(believers, 8) : topPets(believers, 8);
  const dontValue = (dd.netAffinity?.dontValue || []).slice(0, 6);

  return (
    <div style={{ opacity: dormant ? 0.94 : 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
        <span style={chip(r.color)}>● {r.label}</span>
        <span style={{ fontSize: SZ.micro, color: C.text.faint }}>{r.hint}{dd.reachability?.daysSinceLastMove != null ? ` · ${dd.reachability.daysSinceLastMove}d ago` : ""}</span>
        {conf && <span style={chip(conf.color)}>{conf.label}</span>}
        {crown
          ? <span style={{ ...chip(C.brand.orange), marginLeft: "auto" }} title={dd.archetype.basis}>🏆 {crown}</span>
          : <span style={{ marginLeft: "auto", fontSize: SZ.micro, color: C.text.faint, fontStyle: "italic" }}>no clear archetype — {dd.archetype?.reason}</span>}
      </div>
      {dormant && <div style={{ fontSize: SZ.small, color: C.status.warning, margin: "2px 0 10px" }}>Hard to reach right now — fingerprint shown for reference.</div>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 8 }}>
        <div>
          <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
            {["leverage", "pets"].map((t) => (
              <button key={t} onClick={() => setTab(t)} style={{ cursor: "pointer", fontSize: SZ.micro, fontWeight: 700, padding: "3px 10px", borderRadius: 999, border: `1px solid ${tab === t ? C.brand.orange : C.border.default}`, background: tab === t ? `${C.brand.orange}1f` : "transparent", color: tab === t ? C.brand.accent : C.text.muted }}>
                {t === "leverage" ? "Leverage believers" : "Pet players"}
              </button>
            ))}
          </div>
          <div style={{ fontSize: SZ.micro, color: C.text.faint, marginBottom: 4 }}>{tab === "leverage" ? "Valuable players he'll overpay for (rate × value)" : "Cheap guys he hoards everywhere"}</div>
          {list.length ? list.map((b) => <BelieverRow key={b.player_id} b={b} showAffinity={tab === "leverage"} />) : <div style={{ fontSize: SZ.small, color: C.text.faint }}>No clear believers.</div>}
        </div>
        <div>
          <div style={{ fontSize: SZ.micro, color: C.text.muted, marginBottom: 6, fontWeight: 700 }}>DON'T-VALUE — most valuable assets he's dealt away</div>
          {dontValue.length ? dontValue.map((d) => (
            <div key={d.player_id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0", fontSize: SZ.small }}>
              <span style={posChip(d.position)}>{d.position}</span>
              <span style={{ color: C.text.primary, fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</span>
              <span style={{ color: C.extended.valueHighlight }}>{d.value != null ? d.value : "—"}</span>
            </div>
          )) : <div style={{ fontSize: SZ.small, color: C.text.faint }}>No skill players netted away.</div>}
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: SZ.micro, color: C.text.muted, fontWeight: 700, marginBottom: 8 }}>MANAGER PROFILE</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {axisRows(dd).map((row) => {
            const col = bandColor(row.band);
            return (
              <div key={row.key} style={{ background: C.bg.raised, border: `1px solid ${C.border.subtle}`, borderRadius: theme.radius.lg, padding: "8px 12px", minWidth: 150, flex: "1 1 150px" }}>
                <div style={{ fontSize: SZ.micro, color: C.text.faint, marginBottom: 4 }}>{row.label}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: SZ.body, fontWeight: 800, color: col, textTransform: "capitalize" }}>{row.value}</span>
                  {row.fragile && <span title={row.hint} style={{ fontSize: 11, color: C.status.warning }}>⚠</span>}
                </div>
                <div style={{ fontSize: SZ.micro, color: C.text.faint, marginTop: 2 }}>{row.hint}</div>
              </div>
            );
          })}
        </div>
        <div style={{ fontSize: SZ.micro, color: C.text.faint, marginTop: 8 }}>{dd.txLeagueCount} dynasty/keeper league{dd.txLeagueCount === 1 ? "" : "s"} sampled · draft posture deferred</div>
      </div>
    </div>
  );
}

// ── Scan member row ────────────────────────────────────────────────────────
function MemberRow({ member, facet, maps, onOpenFacet, onExpandRoster }) {
  const [rosterOpen, setRosterOpen] = useState(false);
  const s = member.standing;
  const standing = `${s.wins}-${s.losses}${s.ties ? `-${s.ties}` : ""} · ${s.fpts.toFixed(1)} PF`;
  const open = facet?.open;
  const pmap = maps.playersMap;

  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontFamily: F, fontSize: SZ.sub, fontWeight: 800, color: C.text.primary }}>{member.display_name}</span>
        {member.team_name && member.team_name !== member.display_name && <span style={{ fontSize: SZ.small, color: C.text.faint }}>“{member.team_name}”</span>}
        <span style={{ marginLeft: "auto", fontSize: SZ.small, color: C.text.secondary, fontVariantNumeric: "tabular-nums" }}>{standing}</span>
      </div>

      <button onClick={() => { setRosterOpen((v) => !v); if (!rosterOpen) onExpandRoster(); }}
        style={{ marginTop: 6, background: "none", border: "none", color: C.text.muted, cursor: "pointer", fontSize: SZ.small, padding: 0 }}>
        {rosterOpen ? "▾" : "▸"} Roster here ({member.players.length})
      </button>
      {rosterOpen && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 6 }}>
          {member.players.map((pid) => {
            const m = pmap?.[pid];
            const nm = m ? (`${m.first_name || ""} ${m.last_name || ""}`.trim() || pid) : null;
            return <span key={pid} style={posChip(m?.position)}>{nm || (pmap ? pid : "…")}</span>;
          })}
          {!pmap && <span style={{ fontSize: SZ.micro, color: C.text.faint }}>loading names…</span>}
        </div>
      )}

      <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
        {FACETS.map((f) => {
          const active = open === f.key;
          const computed = f.key === "fingerprint";
          return (
            <button key={f.key} onClick={() => onOpenFacet(member, f.key)}
              style={{ cursor: "pointer", fontSize: SZ.small, fontWeight: 700, padding: "6px 12px", borderRadius: theme.radius.lg,
                border: `1px solid ${active ? C.brand.orange : (computed ? `${C.brand.orange}55` : C.border.default)}`,
                background: active ? `${C.brand.orange}1f` : (computed ? `${C.brand.orange}10` : "transparent"),
                color: active ? C.brand.accent : (computed ? C.brand.accent : C.text.secondary) }}>
              {f.label}{active ? " ▾" : ""}
            </button>
          );
        })}
      </div>

      {open && (
        <div style={{ marginTop: 12, borderTop: `1px solid ${C.border.subtle}`, paddingTop: 12 }}>
          {open === "otherLeagues" && <OtherLeaguesPanel state={facet.otherLeagues} />}
          {open === "trades" && <TradeLogPanel state={facet.txn} maps={maps} />}
          {open === "waivers" && <WaiverLogPanel state={facet.txn} maps={maps} />}
          {open === "fingerprint" && <FingerprintPanel state={facet.fingerprint} affinityMeta={facet.affinityMeta} />}
        </div>
      )}
    </div>
  );
}

export default function ScoutPage() {
  const s = useScout();
  useEffect(() => { document.title = "Mock 1 — Leaguemate Scouting"; }, []);

  return (
    <div style={{ minHeight: "100vh", background: C.bg.app, color: C.text.primary, fontFamily: theme.fonts.body }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 20px 80px" }}>
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontFamily: F, fontSize: SZ.hero, fontWeight: 900, background: C.brand.gradient, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Leaguemate Scouting</div>
          <div style={{ fontSize: SZ.body, color: C.text.secondary, marginTop: 4 }}>Scan your league, then drill into any member's raw activity — trades, waivers, other leagues — or open their computed fingerprint.</div>
        </div>

        {s.phase === "entry" && (
          <div style={{ ...card, maxWidth: 560 }}>
            <label style={{ fontSize: SZ.small, color: C.text.secondary }}>Sleeper username or league ID</label>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <input value={s.entryValue} onChange={(e) => s.setEntryValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && s.submitEntry()} placeholder="e.g. ZeeKill  or  1314040141014122496"
                style={{ flex: 1, padding: "10px 12px", fontSize: SZ.body, borderRadius: theme.radius.lg, background: C.bg.deep, border: `1px solid ${C.border.default}`, color: C.text.primary }} />
              <button onClick={s.submitEntry} disabled={s.busy || !s.entryValue.trim()} style={{ padding: "10px 18px", fontSize: SZ.body, fontWeight: 800, borderRadius: theme.radius.lg, border: "none", background: C.brand.gradientBtn, color: "#fff", cursor: s.busy ? "default" : "pointer", opacity: s.busy ? 0.7 : 1 }}>{s.busy ? "…" : "Scan"}</button>
            </div>
            {s.error && <div style={{ marginTop: 10, fontSize: SZ.small, color: C.status.danger }}>{s.error}</div>}
            <div style={{ marginTop: 12, fontSize: SZ.micro, color: C.text.faint }}>Sleeper-only. Public, read-only data. Scan reads just this league — drill-downs load on click.</div>
          </div>
        )}

        {s.phase === "picking" && (
          <div style={{ ...card, maxWidth: 700 }}>
            <div style={{ fontSize: SZ.sub, fontWeight: 800, marginBottom: 10 }}>Pick the league to scout</div>
            <div style={{ display: "grid", gap: 8 }}>
              {s.userLeagues.map((l) => (
                <button key={l.league_id} onClick={() => s.pickLeague(l.league_id)} style={{ textAlign: "left", padding: "10px 14px", borderRadius: theme.radius.lg, border: `1px solid ${C.border.default}`, background: C.bg.raised, color: C.text.primary, cursor: "pointer" }}>
                  <span style={{ fontWeight: 700, fontSize: SZ.body }}>{l.name}</span><span style={{ fontSize: SZ.micro, color: C.text.faint, marginLeft: 8 }}>{l.season} · {l.total_rosters} teams</span>
                </button>
              ))}
            </div>
            <button onClick={s.back} style={{ marginTop: 12, background: "none", border: "none", color: C.text.muted, cursor: "pointer", fontSize: SZ.small }}>← back</button>
          </div>
        )}

        {s.phase === "scanning" && (
          <div style={{ ...card }}><div style={{ fontSize: SZ.body, fontWeight: 700, marginBottom: 6 }}>Reading league…</div><Bar label={s.progress.message} current={s.progress.current} total={s.progress.total} /></div>
        )}

        {s.phase === "scan" && s.target && (
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
              <span style={{ fontFamily: F, fontSize: SZ.head, fontWeight: 800 }}>{s.target.name}</span>
              <span style={{ fontSize: SZ.small, color: C.text.faint }}>{s.members.length} members · standings from this league · drill into any member</span>
              <button onClick={s.back} style={{ marginLeft: "auto", background: "none", border: `1px solid ${C.border.default}`, color: C.text.muted, cursor: "pointer", fontSize: SZ.small, padding: "5px 12px", borderRadius: theme.radius.md }}>↻ new scan</button>
            </div>
            <div style={{ display: "grid", gap: 12 }}>
              {s.members.map((m) => (
                <MemberRow key={m.user_id} member={m} facet={s.facets[m.user_id]} maps={s.maps} onOpenFacet={s.openFacet} onExpandRoster={s.ensureNames} />
              ))}
            </div>
            <div style={{ ...card, marginTop: 24, textAlign: "center", background: C.bg.deep }}>
              <div style={{ fontSize: SZ.body, fontWeight: 700 }}>Want these reads before every trade deadline?</div>
              <div style={{ fontSize: SZ.small, color: C.text.secondary, marginTop: 4 }}>Mock1 is free — drop your email in the Almanac to hear when the Trade Designer ships.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
